import React, { useEffect, useRef, useState } from 'react';
import { Maximize2, Minimize2, RotateCw, Box, Download } from 'lucide-react';
import styles from './GlbViewer.module.css';

// Ensure model-viewer script is loaded once
let modelViewerLoaded = false;
function ensureModelViewer(): Promise<void> {
  if (modelViewerLoaded) return Promise.resolve();
  if (document.querySelector('script[src*="model-viewer"]')) {
    modelViewerLoaded = true;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
    script.onload = () => {
      modelViewerLoaded = true;
      resolve();
    };
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}

interface GlbViewerProps {
  /** GLB source — data URL or https URL */
  src: string;
  /** Display name */
  name?: string;
  /** Compact mode for chat inline */
  compact?: boolean;
  className?: string;
}

export const GlbViewer: React.FC<GlbViewerProps> = ({
  src,
  name,
  compact = true,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    ensureModelViewer().then(() => setReady(true));
  }, []);

  // Convert data URL to blob URL for model-viewer (handles large files better)
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!src.startsWith('data:')) {
      setBlobUrl(null);
      return;
    }
    try {
      const [header, b64] = src.split(',');
      const mime = header.match(/:(.*?);/)?.[1] || 'model/gltf-binary';
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    } catch {
      setBlobUrl(null);
    }
  }, [src]);

  const modelSrc = blobUrl || src;
  const displayName = name || 'GLB Model';
  const ext = name?.split('.').pop()?.toUpperCase() || 'GLB';

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = modelSrc;
    a.download = name || 'model.glb';
    a.click();
  };

  if (error) {
    return (
      <div className={`${styles.fallback} ${className || ''}`}>
        <Box size={28} strokeWidth={1.5} />
        <span>{displayName}</span>
        <span className={styles.errorText}>Failed to load 3D model</span>
      </div>
    );
  }

  const viewerHeight = expanded ? '480px' : compact ? '240px' : '360px';

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${expanded ? styles.expanded : ''} ${compact ? styles.compact : ''} ${className || ''}`}
    >
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <span className={styles.modelName}>
          <span className={styles.modelIcon}>
            <Box size={12} strokeWidth={2} />
          </span>
          {displayName}
        </span>
        <div className={styles.actions}>
          <button
            className={styles.actionBtn}
            onClick={handleDownload}
            title="Download"
          >
            <Download size={13} />
          </button>
          <button
            className={styles.actionBtn}
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
        </div>
      </div>

      {/* Viewer */}
      {ready ? (
        <div className={styles.viewerWrap}>
          {/* @ts-expect-error model-viewer is a web component */}
          <model-viewer
            src={modelSrc}
            alt={displayName}
            auto-rotate
            camera-controls
            touch-action="pan-y"
            shadow-intensity="1.2"
            shadow-softness="0.8"
            exposure="1.1"
            environment-image="neutral"
            style={{
              width: '100%',
              height: viewerHeight,
              background: 'transparent',
              '--poster-color': 'transparent',
            }}
            onError={() => setError(true)}
          />
          <div className={styles.badge}>
            <Box size={9} />
            {ext}
          </div>
          <div className={styles.controls}>
            <button
              className={styles.actionBtn}
              onClick={() => setExpanded(!expanded)}
              title={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.loading}>
          <RotateCw size={22} className={styles.spinner} />
          <span>Loading 3D viewer...</span>
        </div>
      )}
    </div>
  );
};

/** Check if a filename or data URL represents a GLB/GLTF file */
export function isGlbFile(nameOrUrl: string): boolean {
  const lower = nameOrUrl.toLowerCase();
  return (
    lower.endsWith('.glb') ||
    lower.endsWith('.gltf') ||
    lower.includes('model/gltf-binary') ||
    lower.includes('model/gltf+json')
  );
}
